import "./style.scss";
import { FC, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/src/store";
import {
  setColumnWidth,
  setRowFieldValue,
  toggleRowSelection,
  toggleAllRows,
} from "@/src/store/slices/AdmissionOfficeDatabase/AODatabaseTableSlice";
import { IRowData } from "@/src/types/applicationOnline";
import { IADatabaseDocument, IADatabaseFilters } from "@/src/types/admissionDatabase";
import ArrowFilter from "@/src/svg/applicationsPage/ArrowFilter";
import VerticalLine from "../../VerticalLine/VerticalLine";
import GuidelineSvg from "@/src/svg/sidebar/GuidelineSvg";
import AODFilterPopUp from "../AODFilterPopUp/AODFilterPopUp";
import {
  admissionDatabaseIsAvailableConst,
  resolveAdmissionDatabaseIsAvailableId,
  resolveAdmissionDatabaseIsAvailableLabel,
} from "@/src/constants/AdmissionOffice/admissionOfficeDatabaseConsts";
import { admissionDatabaseRequests } from "@/src/api/admissionOfficeDatabaseRequests";

// vocabulary id → accessor for data
const columnAccessors: Record<string, (row: IADatabaseDocument) => React.ReactNode> = {
  data_added: (row) => row.data_added,
  full_name: (row) => row.full_name,
  title: (row) => row.title,
  document_type: (row) => row.document_type,
  document_seria: (row) => row.document_seria,
  date_of_birth: (row) => row.date_of_birth,
  nationality: (row) => row.nationality,
  course: (row) => row.course,
  whose_doc: (row) => row.whose_doc,
};

interface APBodyProps {
  headerCheckboxRef: React.RefObject<HTMLInputElement | null>;
  setIsApPopUpFullName: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentDataPopUp: React.Dispatch<React.SetStateAction<IRowData | undefined>>;
  setIsCurrentPage: React.Dispatch<React.SetStateAction<string>>;

  tableFiltersList: IADatabaseFilters | undefined;
  isShowSettingsModal: boolean;
}

const AODBody: FC<APBodyProps> = ({
  headerCheckboxRef,
  setIsCurrentPage,
  tableFiltersList,
  isShowSettingsModal,
}) => {
  const dispatch = useDispatch();
  const { data, visibleColumns, selectedRows } = useSelector((state: RootState) => state.AODatabaseTable);
  const [filterPopUp, setFilterPopUp] = useState<string | null>(null);
  const [openTableParamMenu, setOpenTableParamMenu] = useState<string>("");
  const [openTableParamMenuRowId, setOpenTableParamMenuRowId] = useState<number | null>(null);
  const allChecked = selectedRows.length === data.length && data.length > 0;
  const someChecked = selectedRows.length > 0 && selectedRows.length < data.length;

  const [fixedColumnIndex, setFixedColumnIndex] = useState<number | null>(null);
  const [isAttach, setIsAttach] = useState<number | null>(null);

  const handleAttach = (index: number) => {
    if (isShowSettingsModal) {
      setFixedColumnIndex((prev) => (prev === index ? null : index));
      setIsAttach(index);
      if (isAttach === index) {
        setIsAttach(null);
      }
    }
  };

  const CHECKER_WIDTH = 4.12;

  const getStickyStyles = (index: number, section: "thead" | "tbody") => {
    if (fixedColumnIndex === null || index > fixedColumnIndex) return {};
    const left = CHECKER_WIDTH + visibleColumns.slice(0, index).reduce((acc, col) => acc + (col.width || 6), 0);
    return {
      position: "sticky" as const,
      left: `${left}rem`,
      zIndex: section === "thead" ? 7 : 5,
    };
  };

  const dragData = useRef<{ startX: number; thId: string | null; startWidth: number }>({
    startX: 0,
    thId: null,
    startWidth: 0,
  });

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>, thId: string, startWidth: number) => {
    if (!isShowSettingsModal) return;
    e.stopPropagation();
    e.preventDefault();
    dragData.current = { startX: e.clientX, thId, startWidth };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const onMouseMove = (e: MouseEvent) => {
    if (!isShowSettingsModal || !dragData.current.thId) return;
    const deltaRem = (e.clientX - dragData.current.startX) / 16;
    let newWidth = dragData.current.startWidth + deltaRem;
    if (newWidth < 2) newWidth = 2;
    dispatch(setColumnWidth({ id: dragData.current.thId, width: newWidth }));
  };

  const onMouseUp = () => {
    if (!isShowSettingsModal) return;
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", onMouseUp);
    dragData.current = { startX: 0, thId: null, startWidth: 0 };
  };

  // ====== drag-to-scroll (horizontal, thead only) ======
  const scrollRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const scrollStartX = useRef(0);
  const scrollStartScrollLeft = useRef(0);

  const isTheadScrollTarget = (target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) return false;
    return (
      !target.closest("input") &&
      !target.closest(".custom-checkbox") &&
      !target.closest(".cell-wrapper-arrow") &&
      !target.closest(".cell-wrapper-attach") &&
      !target.closest(".vertical-line") &&
      !target.closest(".admission-database-filter")
    );
  };

  const onTheadScrollMouseDown = (e: React.MouseEvent) => {
    if (isShowSettingsModal || !scrollRef.current || !isTheadScrollTarget(e.target)) return;
    isDragging.current = true;
    scrollStartX.current = e.clientX;
    scrollStartScrollLeft.current = scrollRef.current.scrollLeft;
  };

  const onTheadScrollMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current || !scrollRef.current) return;
    e.preventDefault();
    const dx = e.clientX - scrollStartX.current;
    scrollRef.current.scrollLeft = scrollStartScrollLeft.current - dx;
  };

  const stopTheadScrollDrag = () => {
    isDragging.current = false;
  };

  const onTheadScrollTouchStart = (e: React.TouchEvent) => {
    if (isShowSettingsModal || !scrollRef.current || !isTheadScrollTarget(e.target)) return;
    isDragging.current = true;
    scrollStartX.current = e.touches[0].clientX;
    scrollStartScrollLeft.current = scrollRef.current.scrollLeft;
  };

  const onTheadScrollTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current || !scrollRef.current) return;
    const dx = e.touches[0].clientX - scrollStartX.current;
    scrollRef.current.scrollLeft = scrollStartScrollLeft.current - dx;
  };

  const replacingBackendValueToCustom: Record<string, string> = {
    APPLY_ONLINE: "Apply Online",
    NEW: "New",
  };

  const toggleTableParamMenu = (menuType: string, rowId: number) => {
    if (openTableParamMenu === menuType && openTableParamMenuRowId === rowId) {
      setOpenTableParamMenu("");
      setOpenTableParamMenuRowId(null);
    } else {
      setOpenTableParamMenu(menuType);
      setOpenTableParamMenuRowId(rowId);
    }
  };

  const handleIsAvailableChange = async (
    event: React.MouseEvent<HTMLDivElement>,
    rowId: number,
    value: "true" | "false",
  ) => {
    event.stopPropagation();
    setOpenTableParamMenu("");
    setOpenTableParamMenuRowId(null);

    const nextValue = value === "true";
    const targetRow = data.find((item) => item.id === rowId);
    const currentValue = targetRow?.is_available !== false;

    if (currentValue === nextValue) return;

    // Optimistic update for responsive UI.
    dispatch(setRowFieldValue({ id: rowId, field: "is_available", value: nextValue }));

    const response = await admissionDatabaseRequests.updateDocumentAvailability(rowId, nextValue);
    if (!response) {
      // Revert on failure to keep UI consistent with backend.
      dispatch(setRowFieldValue({ id: rowId, field: "is_available", value: currentValue }));
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".status-menu") && !target.closest(".is-available-option")) {
        setOpenTableParamMenu("");
        setOpenTableParamMenuRowId(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  return (
    <div
      className={`admission-database-body ${isShowSettingsModal ? "settings-mode" : ""}`}
      ref={scrollRef}
      onMouseMove={onTheadScrollMouseMove}
      onMouseUp={stopTheadScrollDrag}
      onMouseLeave={stopTheadScrollDrag}
      onTouchMove={onTheadScrollTouchMove}
      onTouchEnd={stopTheadScrollDrag}
      onTouchCancel={stopTheadScrollDrag}
    >
      <table>
        <thead onMouseDown={onTheadScrollMouseDown} onTouchStart={onTheadScrollTouchStart}>
          <tr>
            <th id="table-checker" style={{ width: `${CHECKER_WIDTH}rem` }}>
              <div className="cell-wrapper">
                <label className={`custom-checkbox ${someChecked && !allChecked ? "indeterminate" : ""}`}>
                  <input
                    type="checkbox"
                    checked={allChecked}
                    ref={headerCheckboxRef}
                    onChange={() => dispatch(toggleAllRows())}
                  />
                  <span className="checkmark"></span>
                </label>
                <div className="vertical-line">
                  <VerticalLine />
                </div>
              </div>
            </th>
            {visibleColumns.map(({ title, id, width }, index) => (
              <th
                key={id}
                className={id === "is_available" ? "is_available" : undefined}
                style={{
                  width: `${width}rem`,
                  minWidth: `${width}rem`,
                  maxWidth: `${width}rem`,
                  ...getStickyStyles(index, "thead"),
                }}
              >
                <div className="cell-wrapper">
                  <p>{title}</p>
                  <div className="cell-wrapper-actions">
                    {(isShowSettingsModal || isAttach === index) && (
                      <div className="cell-wrapper-attach" onClick={() => handleAttach(index)}>
                        <GuidelineSvg />
                      </div>
                    )}
                    {(id === "data_added" ||
                      id === "title" ||
                      id === "document_type" ||
                      id === "nationality" ||
                      id === "course" ||
                      id === "whose_doc") && (
                      <div
                        className={`cell-wrapper-arrow ${filterPopUp === id ? "rotate" : ""}`}
                        onClick={() => setFilterPopUp(filterPopUp === id ? null : id)}
                      >
                        <ArrowFilter />
                      </div>
                    )}
                  </div>
                  <div
                    className="vertical-line"
                    onMouseDown={isShowSettingsModal ? (e) => onMouseDown(e, id, width || 6) : undefined}
                    style={{ cursor: isShowSettingsModal ? "ew-resize" : undefined }}
                  >
                    <VerticalLine />
                  </div>
                  <AODFilterPopUp
                    id={id}
                    title={title}
                    tableFiltersList={tableFiltersList}
                    filterPopUp={filterPopUp}
                    setFilterPopUp={setFilterPopUp}
                    setIsCurrentPage={setIsCurrentPage}
                  />
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr key={row.id} className={row.id != null && selectedRows.includes(row.id) ? "table-row-selected" : undefined}>
              <td id="table-checker" style={{ width: `${CHECKER_WIDTH}rem` }}>
                <div className="cell-wrapper">
                  <label className="custom-checkbox">
                    <input
                      type="checkbox"
                      checked={row.id != null && selectedRows.includes(row.id)}
                      onChange={() => {
                        if (row.id != null) dispatch(toggleRowSelection(row.id));
                      }}
                    />
                    <span className="checkmark"></span>
                  </label>
                  <div className="vertical-line">
                    <VerticalLine />
                  </div>
                </div>
              </td>
              {visibleColumns.map(({ id, width }, index) => {
                const rawValue = String(columnAccessors[id]?.(row) ?? "");
                const displayValue = replacingBackendValueToCustom[rawValue] ?? rawValue;
                const isAvailableStatusId = resolveAdmissionDatabaseIsAvailableId(row.is_available);
                const isAvailableStatusLabel = resolveAdmissionDatabaseIsAvailableLabel(row.is_available);

                return (
                  <td
                    key={id}
                    className={id === "is_available" ? "is_available" : undefined}
                    style={{
                      width: `${width}rem`,
                      minWidth: `${width}rem`,
                      maxWidth: `${width}rem`,
                      ...getStickyStyles(index, "tbody"),
                    }}
                  >
                    <div
                      className={`cell-wrapper ${id === "full_name" ? "clicable" : ""} ${
                        id === "is_available" ? "is-available-cell clicable" : ""
                      }`}
                      {...(id === "is_available" && row.id != null
                        ? {
                            onClick: (e: React.MouseEvent) => {
                              e.stopPropagation();
                              toggleTableParamMenu("is_available", row.id!);
                            },
                          }
                        : {})}
                    >
                      {id === "is_available" ? (
                        <>
                          <div className={`status-option is-available-option ${isAvailableStatusId}`}>
                            <span className="status-label">{isAvailableStatusLabel}</span>
                          </div>
                          {openTableParamMenu === "is_available" &&
                            openTableParamMenuRowId === row.id && (
                              <div className="status-menu">
                                {admissionDatabaseIsAvailableConst.map(({ id: optionId, label }) => (
                                  <div
                                    key={optionId}
                                    className={`status-option is-available-option ${optionId}`}
                                    onClick={(e) => {
                                      if (row.id != null) {
                                        handleIsAvailableChange(e, row.id, optionId);
                                      }
                                    }}
                                  >
                                    <span className="status-label">{label}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                        </>
                      ) : (
                        <div className="cell-content">{displayValue}</div>
                      )}
                      <div className="vertical-line">
                        <VerticalLine />
                      </div>
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AODBody;
